// ===================================
// TRENCH QUEST - Unified Gemini Client
// Text generation (narrative) + Image generation (Imagen 3)
// ===================================

import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase/config'
import type {
  GeneratedSituation,
  GeneratedOutcome,
  GameMasterContext,
  ImageGenerationRequest,
  GeneratedImage,
} from '@/types'

// Firebase Function references for Game Master
const generateSituationFn = httpsCallable<
  { context: GameMasterContext; systemPrompt: string; userPrompt: string },
  { situation: GeneratedSituation }
>(functions, 'generateSituation')

const generateOutcomeFn = httpsCallable<
  {
    situation: GeneratedSituation
    choiceId: string
    context: GameMasterContext
    systemPrompt: string
    userPrompt: string
  },
  { outcome: GeneratedOutcome }
>(functions, 'generateOutcome')

const generateImageFn = httpsCallable<
  { prompt: string; aspectRatio: string },
  { imageUrl: string; success: boolean }
>(functions, 'generateImage')

/**
 * Unified Gemini Client for Trench Quest
 * Handles both text (narrative) and image (Imagen 3) generation
 */
export class GeminiClient {
  private static instance: GeminiClient
  private imageCache: Map<string, GeneratedImage> = new Map()

  private constructor() {}

  static getInstance(): GeminiClient {
    if (!GeminiClient.instance) {
      GeminiClient.instance = new GeminiClient()
    }
    return GeminiClient.instance
  }

  /**
   * Generate a dynamic situation using the Game Master AI
   */
  async generateSituation(
    context: GameMasterContext,
    systemPrompt: string,
    userPrompt: string
  ): Promise<GeneratedSituation> {
    try {
      const result = await generateSituationFn({ context, systemPrompt, userPrompt })
      return result.data.situation
    } catch (error) {
      console.error('Failed to generate situation:', error)
      throw new Error('Failed to generate game situation')
    }
  }

  /**
   * Generate outcome for a player's choice
   */
  async generateChoiceOutcome(
    situation: GeneratedSituation,
    choiceId: string,
    context: GameMasterContext,
    systemPrompt: string,
    userPrompt: string
  ): Promise<GeneratedOutcome> {
    try {
      const result = await generateOutcomeFn({
        situation,
        choiceId,
        context,
        systemPrompt,
        userPrompt,
      })
      return result.data.outcome
    } catch (error) {
      console.error('Failed to generate outcome:', error)
      throw new Error('Failed to generate choice outcome')
    }
  }

  /**
   * Generate a scene image using Imagen 3
   */
  async generateSceneImage(request: ImageGenerationRequest): Promise<GeneratedImage> {
    const { situation, style, aspectRatio } = request

    // Create cache key from visual tags
    const cacheKey = this.createImageCacheKey(situation.visualTags, style)

    // Check cache first
    const cached = this.imageCache.get(cacheKey)
    if (cached && cached.expiresAt && cached.expiresAt > new Date()) {
      return cached
    }

    try {
      // Build the image prompt
      const prompt = this.buildImagePrompt(situation, style)

      const result = await generateImageFn({ prompt, aspectRatio })

      if (!result.data.success) {
        throw new Error('Image generation failed')
      }

      const generatedImage: GeneratedImage = {
        id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        url: result.data.imageUrl,
        visualTags: situation.visualTags,
        situationId: situation.id,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
      }

      // Cache the result
      this.imageCache.set(cacheKey, generatedImage)

      return generatedImage
    } catch (error) {
      console.error('Failed to generate scene image:', error)

      // Return a placeholder image on failure
      return {
        id: `placeholder-${Date.now()}`,
        url: this.getPlaceholderImage(situation.mood),
        visualTags: situation.visualTags,
        situationId: situation.id,
        createdAt: new Date(),
      }
    }
  }

  /**
   * Build consistent style prompt for Imagen 3
   */
  private buildImagePrompt(situation: GeneratedSituation, style: string): string {
    const stylePrompts: Record<string, string> = {
      pixel_art: `Retro pixel art style inspired by 1990s adventure games like Leisure Suit Larry.
16-bit aesthetic with vibrant colors. Slightly satirical and humorous tone.
Crisp pixel boundaries, limited color palette, dithering effects.`,
      comic: `Comic book illustration style with bold outlines and dynamic composition.
Vibrant colors, expressive characters, dramatic lighting.
Modern graphic novel aesthetic with cyberpunk influences.`,
      realistic: `Photorealistic digital art with cinematic lighting.
High detail, dramatic composition, professional photography quality.
Moody atmosphere with rich shadows and highlights.`,
      vaporwave: `Vaporwave aesthetic with neon colors, grid patterns, and retro-futuristic elements.
Synthwave vibes, glowing effects, 80s nostalgia.
Pink and cyan color scheme, palm trees, sunset gradients.`,
    }

    const moodDescriptions: Record<string, string> = {
      dramatic: 'intense and serious atmosphere, high stakes feeling',
      comedic: 'lighthearted and humorous, absurd but fun',
      tense: 'suspenseful, shadows and uncertainty',
      romantic: 'warm and intimate, soft lighting',
      mysterious: 'enigmatic, fog and shadows, secrets lurking',
      triumphant: 'victorious and powerful, golden lighting',
      dire: 'ominous and dangerous, red warning tones',
    }

    const stylePrefix = stylePrompts[style] || stylePrompts.pixel_art
    const moodDescription = moodDescriptions[situation.mood] || 'neutral atmosphere'

    // Build location description
    const locationMap: Record<string, string> = {
      'trading-terminal': 'a futuristic trading floor with holographic screens and charts',
      'penthouse-day': 'a luxurious penthouse apartment with floor-to-ceiling windows, daytime city view',
      'penthouse-night': 'a luxurious penthouse with city lights twinkling outside at night',
      'yacht-exterior': 'the deck of a luxury yacht on sparkling ocean water',
      'yacht-interior': 'inside a lavish yacht cabin with expensive furniture',
      'fed-building': 'an imposing government building with marble columns',
      'courthouse': 'a dramatic courthouse interior with wooden benches',
      'lambo-dealership': 'a high-end car dealership showcasing exotic supercars',
      'lambo-driving': 'driving a Lamborghini down a coastal highway',
      'singapore-marina': 'the Singapore Marina Bay skyline at night',
      'beach-day': 'a tropical beach with palm trees and crystal blue water',
      'conference-hall': 'a packed crypto conference with booths and crowds',
      'degen-den': 'a dimly lit underground trading room with multiple monitors',
      'hacker-house': 'a chaotic startup house with whiteboards and beanbags',
    }

    const locationDescription = locationMap[situation.location] || situation.location

    // Build character descriptions
    const characterDescriptions = situation.characters
      .map((c) => `${c.title} (${c.personality.slice(0, 2).join(', ')})`)
      .join(', ')

    return `${stylePrefix}

Scene: ${situation.description}
Location: ${locationDescription}
Mood: ${moodDescription}
${characterDescriptions ? `Characters present: ${characterDescriptions}` : ''}
Visual elements: ${situation.visualTags.join(', ')}

Create a single scene illustration suitable for a video game. No text or UI elements.
Focus on atmosphere and storytelling through visual composition.`
  }

  /**
   * Create a cache key from visual tags
   */
  private createImageCacheKey(visualTags: string[], style: string): string {
    const sortedTags = [...visualTags].sort().join('|')
    return `${style}:${sortedTags}`
  }

  /**
   * Get a placeholder image based on mood
   */
  private getPlaceholderImage(_mood: string): string {
    // Create a simple SVG placeholder
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
        <defs>
          <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#1a1a2e"/>
            <stop offset="50%" style="stop-color:#16213e"/>
            <stop offset="100%" style="stop-color:#0f3460"/>
          </linearGradient>
        </defs>
        <rect fill="url(#bg)" width="1920" height="1080"/>
        <text x="960" y="540" font-family="monospace" font-size="24" fill="#ffffff40" text-anchor="middle">
          Loading scene...
        </text>
      </svg>
    `.trim()

    return `data:image/svg+xml,${encodeURIComponent(svg)}`
  }

  /**
   * Clear the image cache
   */
  clearImageCache(): void {
    this.imageCache.clear()
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.imageCache.size,
      entries: Array.from(this.imageCache.keys()),
    }
  }
}

// Export singleton instance
export const geminiClient = GeminiClient.getInstance()

/**
 * Direct text generation for client-side use (non-Firebase)
 * Uses environment variable for API key
 * NOTE: Only use this for non-sensitive generation - prefer Firebase Functions
 */
export async function generateTextDirect(
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY

  if (!apiKey) {
    throw new Error('Gemini API key not configured')
  }

  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              { text: systemPrompt },
              { text: userPrompt },
            ],
          },
        ],
        generationConfig: {
          temperature: 0.8,
          maxOutputTokens: 2048,
          responseMimeType: 'application/json',
        },
      }),
    }
  )

  if (!response.ok) {
    throw new Error(`Gemini API error: ${response.status}`)
  }

  const data = await response.json()
  return data.candidates?.[0]?.content?.parts?.[0]?.text || ''
}

/**
 * Parse JSON from AI response, handling markdown code blocks
 */
export function parseAIJsonResponse<T>(response: string): T {
  // Try to extract JSON from markdown code blocks if present
  const jsonMatch = response.match(/```json\n?([\s\S]*?)\n?```/)
  const jsonStr = jsonMatch?.[1] ?? response

  try {
    return JSON.parse(jsonStr.trim()) as T
  } catch (error) {
    console.error('Failed to parse JSON response:', response)
    throw new Error('Invalid JSON response from AI')
  }
}
